// src/pages/settings/invoices/InvoiceSettingsShell.tsx
import { Flex, Skeleton, SkeletonText } from '@chakra-ui/react'
import { useFormik } from 'formik'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Outlet } from 'react-router-dom'

import {
  InvoiceSettingsSchema,
  INITIAL_INVOICE_SETTINGS_FORM_VALUES,
  INITIAL_INVOICE_SETTINGS_AUTO_INVOICE_VALUES,
  InvoiceSettingsAutoInvoiceSchema,
  normaliseVatId,
} from '@/common/invoiceSettings.schema'

import { toast } from '@/components/ui/toaster/toasterHelpers'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'

import { getMailTemplatePlaceholders } from '@/api/mail-templates.api'
import { getInvoiceSettings, updateInvoiceSettings } from '@/api/settings.api'
import { extractDistinctProperties } from '@/utils/object.utils'

import type {
  InvoiceSettingsAutoInvoiceData,
  InvoiceSettingsFormData,
} from '@/api/dto/settings.dto'
import type { MailTemplatePlaceholder } from '@/common/mail-templates.common'
import { useUserStore } from '@/stores/user.store'

type Ctx = {
  loading: boolean
  saving: boolean
  serverErrors: Record<string, string>
  clearServerError: (field: string) => void

  placeholders: MailTemplatePlaceholder[]

  formFormik: ReturnType<typeof useFormik<InvoiceSettingsFormData>>
  autoFormik: ReturnType<typeof useFormik<InvoiceSettingsAutoInvoiceData>>

  hasFormChanged: boolean
  hasAutoChanged: boolean

  saveForm: (opts?: { force?: boolean }) => Promise<void>
  saveAuto: () => Promise<void>
}

const InvoiceSettingsCtx = createContext<Ctx | null>(null)

export function useInvoiceSettingsCtx() {
  const v = useContext(InvoiceSettingsCtx)
  if (!v) throw new Error('useInvoiceSettingsCtx must be used within InvoiceSettingsShell')
  return v
}

function runningToUi(n: number | null | undefined) {
  const v = n ?? 1
  return String(v).padStart(4, '0')
}
function runningToApi(v: string | number | null | undefined) {
  const raw = String(v ?? '').trim()
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 1
}

export function InvoiceSettingsShell() {
  const [placeholders, setPlaceholders] = useState<MailTemplatePlaceholder[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})

  const [initialForm, setInitialForm] = useState<InvoiceSettingsFormData>(
    INITIAL_INVOICE_SETTINGS_FORM_VALUES
  )
  const [initialAuto, setInitialAuto] = useState<InvoiceSettingsAutoInvoiceData>(
    INITIAL_INVOICE_SETTINGS_AUTO_INVOICE_VALUES
  )

  const formFormik = useFormik<InvoiceSettingsFormData>({
    initialValues: INITIAL_INVOICE_SETTINGS_FORM_VALUES,
    enableReinitialize: true,
    validationSchema: InvoiceSettingsSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: async () => {},
  })

  const autoFormik = useFormik<InvoiceSettingsAutoInvoiceData>({
    initialValues: INITIAL_INVOICE_SETTINGS_AUTO_INVOICE_VALUES,
    enableReinitialize: true,
    validationSchema: InvoiceSettingsAutoInvoiceSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async () => {},
  })

  const formFormikRef = useRef(formFormik)
  const autoFormikRef = useRef(autoFormik)

  useEffect(() => {
    formFormikRef.current = formFormik
  }, [formFormik])

  useEffect(() => {
    autoFormikRef.current = autoFormik
  }, [autoFormik])

  const clearServerError = (field: string) => {
    setServerErrors(prev => {
      const copy = { ...prev }
      delete copy[field]
      return copy
    })
  }
  const INVOICE_KEYS = [
    'bankName',
    'iban',
    'bic',
    'taxNumber',
    'vatId',
    'vatPercentage',
    'invoiceNumberPrefix',
    'offerNumberPrefix',
    'issuerType',
    'accountHolder',
    'firstName',
    'lastName',
    'company',
    'street',
    'streetNumber',
    'postal',
    'city',
    'phone',
    'email',
    'country',
  ] as const

  function pick<T extends object, K extends readonly (keyof any)[]>(obj: T, keys: K): any {
    const out: any = {}
    keys.forEach((k: any) => {
      if (k in obj) out[k] = (obj as any)[k]
    })
    return out
  }

  const hasFormChanged =
    Object.keys(extractDistinctProperties(formFormik.values, initialForm)).length > 0

  const hasAutoChanged =
    Object.keys(extractDistinctProperties(autoFormik.values, initialAuto)).length > 0

  const saveForm = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = !!opts?.force

      const changes = extractDistinctProperties(formFormik.values, initialForm)
      const hasChanges = Object.keys(changes).length > 0

      if (!force && !hasChanges) {
        toast.info('Keine Änderungen gefunden')
        return
      }

      try {
        setSaving(true)
        setServerErrors({})

        // When force=true and no changes, we still send a valid payload.
        // Keep your existing "combined update" behavior.
        const invoicePayload: any = {
          ...pick(initialForm as any, INVOICE_KEYS),
          ...pick((hasChanges ? changes : {}) as any, INVOICE_KEYS),
          ...initialAuto,
        }

        invoicePayload.invoiceNumberPrefix =
          String(invoicePayload.invoiceNumberPrefix ?? '').trim() || 'RE'

        // Normalised here, not in a blur handler: this is the one point every save path goes
        // through, so validation and submission cannot diverge. Relying on blur meant that an
        // Enter-to-submit, or a value set programmatically, posted the raw "DE 123 456 789" —
        // which passed the form's own checks and was then rejected by the backend pattern.
        if (invoicePayload.vatId) {
          invoicePayload.vatId = normaliseVatId(String(invoicePayload.vatId))
        }

        await updateInvoiceSettings(invoicePayload)

        // ✅ confirm migration locally
        localStorage.setItem('sib_invoice_settings_confirmed', '1')

        // ✅ reflect in user store so banner disappears immediately
        const current = useUserStore.getState().user
        if (current && !current.settingsComplete) {
          useUserStore.getState().setUser({ ...current, settingsComplete: true })
        }

        toast.success('Änderungen gespeichert')

        // Only update initialForm if there were actual changes
        if (hasChanges) {
          setInitialForm((prev: InvoiceSettingsFormData) => ({ ...prev, ...changes }))
        }

        // Reset dirty state regardless (confirmation should not keep the form "dirty")
        formFormik.resetForm({ values: formFormik.values })
      } catch (err: any) {
        if (err?.errors) {
          const formatted: Record<string, string> = {}
          Object.entries(err.errors).forEach(([field, messages]) => {
            formatted[field] = Array.isArray(messages) ? messages.join(', ') : String(messages)
          })
          formFormik.setErrors(formatted)
          setServerErrors(formatted)
          toast.error(Object.values(formatted)[0] || 'Fehler beim Speichern')
          return
        }
        toast.error(err?.message || 'Fehler beim Speichern')
      } finally {
        setSaving(false)
      }
    },
    [formFormik, initialForm, initialAuto]
  )

  const saveAuto = useCallback(async () => {
    const changes = extractDistinctProperties(autoFormik.values, initialAuto)
    if (Object.keys(changes).length === 0) return toast.info('Keine Änderungen gefunden')

    try {
      setSaving(true)
      setServerErrors({})

      const payload: any = { ...initialAuto, ...changes, ...initialForm }

      // your rules preserved
      const tpl = String(autoFormik.values.activityInvoiceTemplate ?? '').trim()
      payload.activityInvoiceTemplate = tpl.length > 0 ? tpl : '{PREFIX}{YYYY}{MM}-{RUNNING}'
      payload.activityInvoiceRunningStart = runningToApi(
        autoFormik.values.activityInvoiceRunningStart
      )
      payload.sendAfterAutoGeneration = !!autoFormik.values.sendAfterAutoGeneration

      await updateInvoiceSettings(payload)

      toast.success('Änderungen gespeichert')
      setInitialAuto((prev: InvoiceSettingsAutoInvoiceData) => ({ ...prev, ...changes }))
      autoFormik.resetForm({ values: autoFormik.values })
    } catch (err: any) {
      if (err?.errors) {
        const formatted: Record<string, string> = {}
        Object.entries(err.errors).forEach(([field, messages]) => {
          formatted[field] = Array.isArray(messages) ? messages.join(', ') : String(messages)
        })
        autoFormik.setErrors(formatted)
        setServerErrors(formatted)
        toast.error(Object.values(formatted)[0] || 'Fehler beim Speichern')
        return
      }
      toast.error(err?.message || 'Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }, [autoFormik, initialAuto, initialForm])

  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(
    (formFormik.dirty && hasFormChanged) || (autoFormik.dirty && hasAutoChanged)
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [invoiceSettings, placeholdersRes] = await Promise.all([
        getInvoiceSettings(),
        getMailTemplatePlaceholders(),
      ])

      const {
        autoGenerationDay,
        autoGenerationEnabled,
        autoGenerationTime,
        activityInvoiceResetPolicy,
        activityInvoicePrefix,
        activityInvoiceTemplate,
        activityInvoiceRunningStart,
        invoiceTemplate,
        invoicePreTextTemplate,
        invoicePostTextTemplate,
        sendAfterAutoGeneration,
        ...rest
      } = invoiceSettings

      const safeRest: InvoiceSettingsFormData = {
        ...(rest as InvoiceSettingsFormData),

        // ✅ profile-driven fields (if your form contains them already)
        firstName: (rest as any).firstName ?? '',
        lastName: (rest as any).lastName ?? '',
        company: (rest as any).company ?? '',
        street: (rest as any).street ?? '',
        streetNumber: (rest as any).streetNumber ?? '',
        postal: (rest as any).postal ?? '',
        city: (rest as any).city ?? '',
        phone: (rest as any).phone ?? '',
        email: (rest as any).email ?? '',
        country: (rest as any).country ?? '',

        // ✅ invoice-specific defaults preserved
        invoiceNumberPrefix: String((rest as any).invoiceNumberPrefix ?? '').trim() || 'RE',

        // ✅ NEW invoice fields (coming from backend soon)
        issuerType: (rest as any).issuerType ?? 'PERSON',
        accountHolder: (rest as any).accountHolder ?? '',
      }

      const autoValues: InvoiceSettingsAutoInvoiceData = {
        autoGenerationEnabled,
        autoGenerationDay,
        autoGenerationTime,
        activityInvoiceResetPolicy: activityInvoiceResetPolicy ?? 'YEARLY',
        activityInvoicePrefix: activityInvoicePrefix ?? '',
        activityInvoiceTemplate: activityInvoiceTemplate ?? '',
        activityInvoiceRunningStart: runningToUi(activityInvoiceRunningStart),
        invoiceTemplate,
        sendAfterAutoGeneration: invoiceSettings.sendAfterAutoGeneration ?? true,
        invoicePreTextTemplate: invoicePreTextTemplate ?? '',
        invoicePostTextTemplate: invoicePostTextTemplate ?? '',
      }

      formFormikRef.current.setValues(safeRest)
      autoFormikRef.current.setValues(autoValues)

      setInitialForm(safeRest)
      setInitialAuto(autoValues)

      setPlaceholders(placeholdersRes.placeholders)
    } catch (err: any) {
      toast.error(err?.message || 'Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const ctx: Ctx = useMemo(
    () => ({
      loading,
      saving,
      serverErrors,
      clearServerError,
      placeholders,
      formFormik,
      autoFormik,
      hasFormChanged,
      hasAutoChanged,
      saveForm,
      saveAuto,
    }),
    [
      loading,
      saving,
      serverErrors,
      placeholders,
      formFormik,
      autoFormik,
      hasFormChanged,
      hasAutoChanged,
      saveForm,
      saveAuto,
    ]
  )

  if (loading) {
    return (
      <Flex direction="column" w="full" gap={4}>
        <SkeletonText noOfLines={1} w="220px" h={6} />
        <SkeletonText noOfLines={1} w="full" h={4} />
        <Skeleton h="40px" />
        <Skeleton h="40px" />
        <Skeleton h="40px" />
      </Flex>
    )
  }

  return (
    <InvoiceSettingsCtx.Provider value={ctx}>
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={confirmNavigation}
        onNavigate={cancelNavigation}
        onSave={async () => {
          if (formFormik.dirty && hasFormChanged) await saveForm()
          if (autoFormik.dirty && hasAutoChanged) await saveAuto()
          confirmNavigation()
        }}
      />

      <Outlet />
    </InvoiceSettingsCtx.Provider>
  )
}
